import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, InputNumber, Modal, Select, Space, message } from 'antd';
import axios from 'axios';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token') || ''}` });
const failure = error => error.response?.data?.message || error.message || '操作失败，请重试';
const cellStyle = { minWidth: 76, border: '1px solid #aac8ec', textAlign: 'center', borderRadius: 3, overflow: 'hidden' };
function SeatPreview({ plan }) {
  const { layout, seats } = plan;
  const map = new Map(seats.map(s => [`${s.row}:${s.col}`, s]));
  return <div style={{ overflowX: 'auto', padding: '18px 0' }}>
    <div style={{ minWidth: layout.cols * 88 + layout.aislePositions.length * 22 + 40 }}>
      <div style={{ textAlign: 'center', color: '#8a95a6', paddingBottom: 12 }}>教室后方</div>
      {Array.from({ length: layout.rows }, (_, i) => <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <div style={{ width: 40, flexShrink: 0, alignSelf: 'center', color: '#8a95a6', fontSize: 12 }}>第{layout.rows - i}排</div>
        {Array.from({ length: layout.cols }, (_, j) => {
          const s = map.get(`${i + 1}:${j + 1}`);
          return <React.Fragment key={j}><div style={{ ...cellStyle, flex: 1 }}><div style={{ height: 34, lineHeight: '34px', color: s && !s.studentId ? '#1377de' : undefined }}>{s?.name || '空座'}</div><div style={{ background: '#edf5ff', color: '#527497', fontSize: 12, lineHeight: '24px' }}>{s ? `座号 ${s.seatNumber}` : '—'}</div></div>{layout.aislePositions.includes(j + 1) && <div style={{ width: 22, flexShrink: 0, background: '#eff2f6' }} />}</React.Fragment>;
        })}
      </div>)}
      <div style={{ position: 'relative', paddingTop: 10, textAlign: 'center' }}><span style={{ position: 'absolute', [layout.frontDoor === 'left' ? 'left' : 'right']: 10, top: 25, color: '#8a95a6' }}>前门</span><span style={{ display: 'inline-block', background: '#dcecff', padding: '12px 90px', color: '#34618e' }}>讲台 ↑</span></div>
    </div>
  </div>;
}
export default function SeatTemplateImport({ classId, className, onSaved }) {
  const [open, setOpen] = useState(false), [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(6), [door, setDoor] = useState('right'), [pattern, setPattern] = useState('');
  const [preview, setPreview] = useState(null), [selected, setSelected] = useState(0), [error, setError] = useState(''), [fileName, setFileName] = useState('');
  const version = useRef(0), fileInput = useRef(null);
  useEffect(() => { version.current++; setOpen(false); setPreview(null); setError(''); setBusy(false); setFileName(''); }, [classId]);
  useEffect(() => () => { version.current++; }, []);
  const endpoint = `/api/v2/seat-templates/${classId}`;
  const plan = preview?.plans[selected];
  const isCurrent = (generation, token) => version.current === generation && localStorage.getItem('token') === token;
  async function download() {
    const generation = version.current, token = localStorage.getItem('token');
    setBusy(true); setError('');
    try {
      const response = await axios.get(`${endpoint}/template`, { headers: headers(), responseType: 'blob', params: { rows, frontDoor: door, customPattern: pattern } });
      if (!isCurrent(generation, token)) return;
      const url = URL.createObjectURL(response.data), a = document.createElement('a');
      a.href = url; a.download = `${className || classId}-座位模板.xlsx`; a.click(); setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (err) {
      if (isCurrent(generation, token)) {
        let text = failure(err);
        try { if (err.response?.data instanceof Blob) text = JSON.parse(await err.response.data.text()).message || text; } catch (_) {}
        if (isCurrent(generation, token)) setError(text);
      }
    } finally { if (isCurrent(generation, token)) setBusy(false); }
  }
  async function upload(event) {
    const file = event.target.files[0]; event.target.value = '';
    if (!file) return;
    if (!/\.xlsx$/i.test(file.name) || file.size > 2 * 1024 * 1024) { setError('请选择2MB以内的.xlsx模板'); return; }
    const generation = version.current, token = localStorage.getItem('token');
    const form = new FormData(); form.append('file', file);
    setBusy(true); setError(''); setPreview(null); setFileName(file.name);
    try {
      const response = await axios.post(`${endpoint}/preview`, form, { headers: headers() });
      if (!response.data.success || !Array.isArray(response.data.data?.plans) || !response.data.data.plans.length) throw new Error(response.data.message || '预览响应异常，请重试');
      if (isCurrent(generation, token)) { setPreview({ ...response.data.data, token }); setSelected(0); }
    } catch (err) { if (isCurrent(generation, token)) setError(failure(err)); }
    finally { if (isCurrent(generation, token)) setBusy(false); }
  }
  function save() {
    if (!plan || plan.errors.length) return;
    const generation = version.current, token = localStorage.getItem('token');
    if (preview.token !== token) { setPreview(null); setError('账号已变化，请重新预览'); return; }
    Modal.confirm({ title: '确认导入座位表', okText: '确认保存', cancelText: '取消', content: <div>
      <p>保存“{plan.sheetName}”的{plan.seats.length}个座位。</p>
      {!!plan.newCount && <p>同时新建{plan.newCount}份基础档案，包含姓名、当前班级和座号；其他资料可后续补充。</p>}
      {!!preview.savedSeatCount && <p>将替换该班级当前座位安排。</p>}
      {!!plan.unassignedCount && <p>{plan.unassignedCount}名已有学生本次未排座，档案仍保留。</p>}
    </div>, onOk: async () => {
      if (!isCurrent(generation, token)) return;
      setBusy(true); setError('');
      try {
        const response = await axios.post(`${endpoint}/commit`, { previewToken: plan.previewToken, confirmCreate: true, confirmOverwrite: true }, { headers: headers() });
        if (!response.data.success) throw new Error(response.data.message || '保存失败，请重试');
        if (!isCurrent(generation, token)) return;
        const result = response.data.data;
        message.success(`已保存${result.seatCount}个座位，新建${result.createdCount}份学生档案`);
        setOpen(false); setPreview(null); setFileName(''); onSaved();
      } catch (err) { if (isCurrent(generation, token)) setError(failure(err)); throw err; }
      finally { if (isCurrent(generation, token)) setBusy(false); }
    } });
  }
  return <>
    <Button block onClick={() => { setOpen(true); setError(''); }}>下载/导入座位模板</Button>
    <Modal title={`${className || '当前班级'} · 座位模板`} open={open} width={960} onCancel={() => { if (!busy) { version.current++; setOpen(false); setPreview(null); setFileName(''); } }} footer={plan ? <Button type="primary" loading={busy} disabled={busy || !!plan.errors.length} onClick={save}>确认导入座位表</Button> : null} maskClosable={!busy} closable={!busy}>
      <p style={{ color: '#778497' }}>模板按当前班级实时生成。选择Excel中的组合工作表，填写白色姓名格，蓝色格为座号。</p>
      <Space wrap style={{ marginBottom: 16 }}>
        <span>排数</span><InputNumber min={1} max={50} value={rows} onChange={setRows} disabled={busy} />
        <Select value={door} onChange={setDoor} disabled={busy} style={{ width: 150 }} options={[{ value: 'right', label: '前门在右侧' }, { value: 'left', label: '前门在左侧' }]} />
        <Input style={{ width: 220 }} placeholder="其他组合（可选，如2-3-2）" value={pattern} onChange={e => setPattern(e.target.value.trim())} disabled={busy} maxLength={39} />
        <Button onClick={download} disabled={busy}>下载动态模板</Button>
        <Button type="primary" onClick={() => fileInput.current.click()} disabled={busy} loading={busy}>选择Excel并预览</Button>
      </Space>
      <input ref={fileInput} type="file" accept=".xlsx" onChange={upload} style={{ display: 'none' }} />
      {fileName && <p style={{ color: '#778497' }}>{fileName}</p>}
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 12 }} />}
      {plan && <>
        <Select style={{ width: 240 }} value={selected} disabled={busy} onChange={setSelected} options={preview.plans.map((p, i) => ({ value: i, label: p.sheetName }))} />
        <p style={{ marginTop: 16 }}>共{plan.seats.length}人 · 匹配已有{plan.matchedCount}人 · 新建{plan.newCount}人</p>
        {!!plan.newCount && <Alert type="info" message="新姓名将建立学生基础档案，其他资料可后续补充。" style={{ marginBottom: 8 }} />}
        {plan.errors.map(text => <Alert key={text} type="error" message={text} style={{ marginBottom: 8 }} />)}
        {plan.warnings.map((text, i) => <div key={i} style={{ color: '#a26822' }}>{text}</div>)}
        <div style={{ color: '#778497' }}>教师视角 · 第一排靠近下方讲台</div>
        <SeatPreview plan={plan} />
      </>}
    </Modal>
  </>;
}
